import { AfterContentInit, Component, computed, signal, Signal, WritableSignal, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { OffcanvasService } from '../../../../../_services/offcanvas-service/offcanvas.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { EmployeeApiService } from '../../../../../_services/employee-service/api/employee-api.service';
import { BoFormsModule } from '@bo/ng-forms';
import { CategorySelectMultiComponent } from '../../../../../shared/components/forms/category-select-multi/category-select-multi.component';
import { TransactionFilterParams, TransactionType } from '../../../../../_interfaces/transactions.interface';
import { arrayStringToNumber } from '../../../../../shared/utils/filter-utils';
import { EmployeeSelectMultiComponent } from '../../../../../shared/components/forms/employee-select-multi/employee-select-multi.component';
import { TransactionsApiService } from '../../../../../_services/transactions-service/transactions-api.service';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { DateRangePickerComponent } from '../../../../../shared/components/forms/date-range-picker/date-range-picker.component';
import { generateTransactionFilterGroups } from '../_utils/transactions-utils';
import {
  FilterGroup,
  FilterGroupType,
  FilterOption,
} from '../../../../../shared/components/filter-button/filter-button.component';
import { formatDateSlash } from '../../../../../shared/utils/general-utils';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { OffcanvasCancelBtnComponent } from '../../../../../shared/components/offcanvas/offcanvas-cancel-btn/offcanvas-cancel-btn.component';
import { OffcanvasBaseComponent } from '../../../../../shared/components/offcanvas/offcanvas-base/offcanvas-base.component';

@Component({
  selector: 'app-transactions-export',
  imports: [
    NgSelectModule,
    BoFormsModule,
    CategorySelectMultiComponent,
    EmployeeSelectMultiComponent,
    LoaderComponent,
    DateRangePickerComponent,
    OffcanvasCancelBtnComponent,
    OffcanvasBaseComponent,
  ],
  templateUrl: './transactions-export.component.html',
})
export class TransactionsExportComponent implements AfterContentInit {
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly employeeApiService = inject(EmployeeApiService);
  readonly offcanvasService = inject(OffcanvasService);
  readonly transactionsApiService = inject(TransactionsApiService);
  readonly toastService = inject(ToastService);
  readonly ngbDateParserFormatter = inject(NgbDateParserFormatter);

  protected isLoading: WritableSignal<boolean> = signal(false);
  protected exportParams!: Params;
  protected filterParams: Signal<TransactionFilterParams> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return {
      merchant: queryParams['merchant'] ?? '',
      status: queryParams['status']?.split(',') ?? [],
      receipt: queryParams['receipt'] ?? '',
      date_from: queryParams['date_from'] ?? null,
      date_to: queryParams['date_to'] ?? null,
      category: queryParams['category']?.split(',') ?? [],
      employee: queryParams['employee']?.split(',') ?? [],
      type: queryParams['type'] ?? TransactionType.PAY,
    };
  });

  selectedCategories: WritableSignal<number[]> = signal([]);
  selectedEmployees: WritableSignal<string[]> = signal([]);

  protected fromDate: NgbDateStruct | null = null;
  protected toDate: NgbDateStruct | null = null;
  protected filterGroups: Array<FilterGroup> = [];
  protected merchant: string | null = null;
  protected allDates: boolean = false;

  protected returnUrl: string = '/accounting/transactions';

  cleanUpObject(params: Params) {
    const newParams: Params = {};
    for (const key of Object.keys(params)) {
      const val = params[key];
      if (val && val.length > 0) {
        newParams[key] = val;
      }
    }
    return newParams;
  }

  ngAfterContentInit(): void {
    this.selectedCategories.set(arrayStringToNumber(this.filterParams().category ?? []));
    this.selectedEmployees.set(this.filterParams().employee ?? []);
    this.fromDate = this.ngbDateParserFormatter.parse(this.filterParams().date_from);
    this.toDate = this.ngbDateParserFormatter.parse(this.filterParams().date_to);
    this.allDates = !this.toDate && !this.fromDate;
    this.merchant = this.filterParams().merchant ?? '';
    const statusQuery: string[] = this.filterParams().status ?? [];
    const receiptQuery: string = this.filterParams().receipt ?? '';
    this.filterGroups = generateTransactionFilterGroups(receiptQuery, statusQuery);
    this.exportParams = this.cleanUpObject(this.filterParams());
  }

  setFromDate(dateString: string | null) {
    this.fromDate = this.ngbDateParserFormatter.parse(formatDateSlash(dateString));
    if (dateString) {
      this.exportParams['date_from'] = formatDateSlash(dateString);
      this.toDate = null;
    }
  }

  setToDate(dateString: string | null) {
    this.toDate = this.ngbDateParserFormatter.parse(formatDateSlash(dateString));
    if (dateString) this.exportParams['date_to'] = formatDateSlash(dateString);
  }

  updateCategories(event: number[]) {
    this.selectedCategories.set(event);
    this.exportParams['category'] = event;
  }

  updateEmployees(event: string[]) {
    this.selectedEmployees.set(event);
    this.exportParams['employee'] = event;
  }

  updateMerchant(event: string) {
    this.merchant = event;
    this.exportParams['merchant'] = event;
  }

  updateAllDates(event: boolean) {
    this.exportParams['date_from'] = event ? '' : this.ngbDateParserFormatter.format(this.fromDate);
    this.exportParams['date_to'] = event ? '' : this.ngbDateParserFormatter.format(this.toDate);
  }

  selectOption(param: string, option: FilterOption, groupIndex: number, optionIndex: number) {
    if (option.type === FilterGroupType.checkbox) {
      this.filterGroups[groupIndex].options[optionIndex].checked =
        !this.filterGroups[groupIndex].options[optionIndex].checked;
      const newItem = option.value.toString();
      const array = this.exportParams[param] ?? [];
      if (option.checked) {
        array.push(newItem);
      } else {
        array.splice(array.indexOf(newItem), 1);
      }
      this.exportParams[param] = array;
    }
    if (option.type === FilterGroupType.radio) {
      this.exportParams[param] = option.value.toString();
    }
  }

  onExport() {
    this.exportParams = this.cleanUpObject(this.exportParams);
    this.exportParams['type'] = this.filterParams().type
      ? this.filterParams().type?.toString()
      : TransactionType.PAY.toString();
    this.transactionsApiService.exportTransactions({ query: this.exportParams }).subscribe({
      next: (result) => {
        if (result) {
          this.toastService.show(
            'An email with your detailed transactions will be sent to you.',
            'iconoir-check-circle',
          );
          this.offcanvasService.close(this.returnUrl);
        }
      },
      error: () => {
        this.toastService.show('An error occurred while exporting transactions.', 'iconoir-xmark');
      },
    });
  }
}
